<template>
  <div>
    <h2 v-if="!compact" class="mc-h2">Other vehicles involved</h2>
    <p v-if="!compact" class="mc-lede">
      Add every other vehicle in the accident. Leave it empty if no one else was
      involved.
    </p>

    <div v-if="value.vehicles.length">
      <div v-for="(v, i) in value.vehicles" :key="i" class="mc-card">
        <div class="mc-cardhead">
          <b>{{ v.reg || `Vehicle ${i + 1}` }}</b>
          <button
            type="button"
            class="mc-linkbtn mc-danger"
            @click="deleteVehicle(i)"
          >
            Remove
          </button>
        </div>
        <div class="mc-kv">
          <span class="mc-k">Make / model</span
          ><span class="mc-v" :class="{ 'is-empty': !v.makeModel }">{{
            v.makeModel || 'Not given'
          }}</span>
        </div>
        <div class="mc-kv">
          <span class="mc-k">Owner / driver</span
          ><span class="mc-v" :class="{ 'is-empty': !v.owner }">{{
            v.owner || 'Not given'
          }}</span>
        </div>
        <div class="mc-kv">
          <span class="mc-k">Contact</span
          ><span class="mc-v" :class="{ 'is-empty': !v.phone }">{{
            v.phone || 'Not given'
          }}</span>
        </div>
        <div class="mc-kv">
          <span class="mc-k">Their insurer</span
          ><span class="mc-v" :class="{ 'is-empty': !v.insurer }">{{
            v.insurer || 'Unknown'
          }}</span>
        </div>
        <div class="mc-kv">
          <span class="mc-k">Damage</span
          ><span class="mc-v" :class="{ 'is-empty': !v.damage }">{{
            v.damage || 'Not described'
          }}</span>
        </div>
        <button
          type="button"
          class="mc-btn mc-btn--ghost mc-sm mc-wide mc-mt8"
          @click="openEdit(i)"
        >
          Edit details
        </button>
      </div>
    </div>
    <div v-else class="mc-empty">
      <b>No other vehicle added</b>That's fine if yours was the only vehicle
      involved.
    </div>

    <button
      type="button"
      class="mc-btn mc-btn--sec mc-wide mc-mt16"
      @click="openEdit(-1)"
    >
      ＋ Add another vehicle
    </button>

    <h3 class="mc-h3">Other property damaged</h3>
    <div class="mc-field">
      <label class="mc-sr" for="veh-other-property"
        >Other property damaged</label
      >
      <textarea
        id="veh-other-property"
        placeholder="Fence, wall, gate, road sign, kiosk, livestock, street pole… list anything other than a vehicle that was damaged"
        :value="value.otherProperty"
        @input="update('otherProperty', $event.target.value)"
      />
      <p class="mc-hint">Leave blank if only vehicles were involved.</p>
    </div>

    <bottom-sheet :open="sheetOpen" @close="closeSheet">
      <h2 class="mc-h2">
        {{ editingIndex >= 0 ? 'Edit vehicle' : 'Other vehicle' }}
      </h2>
      <p class="mc-lede">Give us whatever you managed to note down.</p>
      <div class="mc-field">
        <label class="mc-lbl" for="veh-reg">Registration number</label
        ><input
          id="veh-reg"
          type="text"
          autocapitalize="characters"
          placeholder="KBX 442Q"
          v-model="draft.reg"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="veh-makemodel">Make and model</label
        ><input
          id="veh-makemodel"
          type="text"
          placeholder="Toyota Hiace"
          v-model="draft.makeModel"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="veh-owner">Owner / driver name</label
        ><input
          id="veh-owner"
          type="text"
          placeholder="Peter Otieno"
          v-model="draft.owner"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="veh-phone">Contact number</label
        ><input
          id="veh-phone"
          type="tel"
          inputmode="tel"
          placeholder="07XX XXX XXX"
          v-model="draft.phone"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="veh-insurer">Their insurer</label
        ><input
          id="veh-insurer"
          type="text"
          placeholder="If they told you"
          v-model="draft.insurer"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="veh-damage">Damage description</label
        ><textarea
          id="veh-damage"
          placeholder="Which parts of their vehicle were damaged?"
          v-model="draft.damage"
        />
      </div>
      <button
        type="button"
        class="mc-btn mc-btn--pri mc-wide"
        @click="saveVehicle"
      >
        {{ editingIndex >= 0 ? 'Save changes' : 'Add vehicle' }}
      </button>
      <button
        type="button"
        class="mc-btn mc-btn--ghost mc-wide mc-mt8"
        @click="closeSheet"
      >
        Cancel
      </button>
    </bottom-sheet>
  </div>
</template>

<script>
import BottomSheet from '../shell/BottomSheet.vue'

const blankVehicle = () => ({
  reg: '',
  makeModel: '',
  owner: '',
  phone: '',
  insurer: '',
  damage: '',
})

export default {
  name: 'OtherVehiclesStep',
  components: { BottomSheet },
  props: {
    // { vehicles: [], otherProperty }
    value: { type: Object, required: true },
    // rendered inside a review card, which already supplies the heading
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      sheetOpen: false,
      editingIndex: -1,
      draft: blankVehicle(),
    }
  },
  methods: {
    update(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
    },
    openEdit(i) {
      this.editingIndex = i
      this.draft = i >= 0 ? { ...this.value.vehicles[i] } : blankVehicle()
      this.sheetOpen = true
    },
    closeSheet() {
      this.sheetOpen = false
    },
    saveVehicle() {
      const o = { ...this.draft, reg: this.draft.reg.toUpperCase() }
      if (!o.reg && !o.owner && !o.makeModel) {
        this.$emit('toast', 'Add at least a registration or a name')
        return
      }
      const vehicles = this.value.vehicles.slice()
      if (this.editingIndex >= 0) vehicles[this.editingIndex] = o
      else vehicles.push(o)
      this.update('vehicles', vehicles)
      this.closeSheet()
      this.$emit(
        'toast',
        this.editingIndex >= 0 ? 'Vehicle updated' : 'Vehicle added'
      )
    },
    deleteVehicle(i) {
      const vehicles = this.value.vehicles.slice()
      vehicles.splice(i, 1)
      this.update('vehicles', vehicles)
    },
  },
}
</script>

<style scoped>
.mc-h2 {
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #14142b;
}

.mc-h3 {
  font-size: 13px;
  font-weight: 600;
  margin: 22px 0 10px;
  color: #3d4450;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 20px;
}

.mc-field {
  margin: 0 0 16px;
}

.mc-lbl {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #3d4450;
  margin-bottom: 7px;
}

/* visually hidden but still readable by screen readers — used where a
   visible heading already duplicates the label text */
.mc-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

input,
textarea {
  width: 100%;
  font-family: inherit;
  font-size: 16px;
  color: #333333;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 13px 14px;
}

textarea {
  min-height: 104px;
  resize: vertical;
  line-height: 1.55;
}

input:focus,
textarea:focus {
  outline: 0;
  border-color: #0066cc;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.13);
}

.mc-hint {
  font-size: 12.5px;
  color: #6e7191;
  margin: 6px 0 0;
}

.mc-wide {
  width: 100%;
}

.mc-mt8 {
  margin-top: 8px;
}

.mc-mt16 {
  margin-top: 16px;
}

.mc-sm {
  padding: 11px 14px;
  font-size: 14px;
}

.mc-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
}

.mc-cardhead {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.mc-cardhead b {
  font-size: 14.5px;
  font-weight: 700;
  color: #14142b;
}

.mc-linkbtn {
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #0052b3;
  cursor: pointer;
  padding: 4px 0;
}

.mc-danger {
  color: #eb5757;
}

.mc-kv {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 8px 0;
  border-bottom: 1px dashed #e8e8e8;
  font-size: 13.5px;
}

.mc-kv:last-child {
  border-bottom: 0;
}

.mc-k {
  color: #6e7191;
  flex: 0 0 42%;
}

.mc-v {
  color: #333333;
  font-weight: 600;
  text-align: right;
  flex: 1;
  overflow-wrap: anywhere;
}

.mc-v.is-empty {
  color: #6e7191;
  font-weight: 400;
  font-style: italic;
}

.mc-empty {
  border: 1.5px dashed #d9dbe9;
  border-radius: 14px;
  padding: 26px 18px;
  text-align: center;
  color: #6e7191;
  font-size: 13.5px;
  background: #fff;
}

.mc-empty b {
  display: block;
  color: #333333;
  font-size: 14.5px;
  margin-bottom: 4px;
}
</style>
